import useGetApi from 'src/api/useGetApi';
import type { PreferredOrderMethod } from './useSuppliers';

export interface SupplierProductDTO {
  id: string;
  name: string;
  sku: string | null;
  description: string | null;
  unitPrice: number;
  product: {
    id: string;
    name: string;
    sku: string | null;
    description: string | null;
  };
  unitVariation: {
    id: string;
    name: string;
    sku: string | null;
    quantityInBaseUnit: number;
    unitOfMeasure: string | null;
    unitCost: number | null;
  };
}

/**
 * Terms for a rebates contract.
 */
export interface RebatesContractTerms {
  /** List of products and shipments covered by the rebate. */
  products: string[];
  /** Supplier name. */
  supplier: string;
  /** Division codes for which the rebate applies. */
  divisions: string[];
  /** Unique identifier for the contract. */
  contractId: string;
  /** Payment terms description (e.g., "1% 1st 2nd Month Following EFT"). */
  paymentTerms: string;
  /** Structure defining rebate tiers, values, and conditions. */
  rebateStructure: RebateTier[];
  /** Date when the contract goes into effect (inclusive), in YYYY-MM-DD format. */
  effectiveStartDate: string;
  /** Date when the contract ends (inclusive), in YYYY-MM-DD format. */
  effectiveEndDate: string;
  /** List of any special conditions or caveats for the rebate. */
  specialConditions: string[];
  /** Confidence level of the data extraction (e.g., "low", "medium", "high"). */
  extractionConfidence: 'low' | 'medium' | 'high';
}

/**
 * A single tier in the rebate structure.
 */
export interface RebateTier {
  /** Name of the rebate tier (e.g., "Base", "Additional", "Maximum"). */
  tier: string;
  /** Rebate percentage or description (e.g., "1%", "2% total"). */
  rebate: string;
  /** Condition under which the rebate applies. */
  condition: string;
}

export interface SupplierDTO {
  id: string;
  name: string;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  address: {
    city: string;
    state: string;
    street: string;
    country: string;
    postalCode: string;
  } | null;
  paymentTerms: string | null;
  preferredOrderMethod: PreferredOrderMethod | null;
  taxId: string | null;
  status: string;
  addresses?: Array<{
    id: string;
    recipientName?: string | null;
    addressLine1: string;
    addressLine2?: string | null;
    locality: string;
    administrativeArea?: string | null;
    postalCode?: string | null;
    countryIso31661Alpha2: string;
    fullNormalizedAddress?: string | null;
  }>;
  aliases?: Array<{
    id: string;
    alias: string;
    addedByUserId?: string | null;
    createdAt: Date;
  }>;
  products?: SupplierProductDTO[];
  contacts?: Array<{
    id: string;
    name: string;
    title?: string | null;
    email?: string | null;
    phone?: string | null;
    isPrimary: boolean;
  }>;
  internalPurchaseOrders?: Array<{
    id: string;
    internalId: number;
    externalId: string;
    orderDate: Date;
    deliveryDate: Date | null;
    status: string;
    totalAmount: number | null;
    lineItems: Array<{
      id: string;
      quantity: number;
      unitPrice: number;
      totalPrice: number;
      product: {
        id: string;
        name: string;
      };
      unitVariation: {
        id: string;
        name: string;
      };
    }>;
  }>;
  metadata?: {
    supplierCode?: string;
    productGroupCode?: string;
    rebateRate?: number;
    lastImportData?: string;
    specialRebateInstructions?: string;
    rebatesContractUrl?: string;
    rebatesContractTerms?: RebatesContractTerms & {
      [key: string]: unknown;
    };
    [key: string]: unknown;
  } | null;
}

export interface QueryParams {
  include?: Array<
    'products' | 'internalPurchaseOrders' | 'contacts' | 'addresses' | 'aliases'
  >;
}

type ApiResponse = {
  supplier: SupplierDTO;
};

const useSupplier = (
  supplierId?: string,
  queryParams?: QueryParams,
  enabled?: boolean,
) => {
  return useGetApi<ApiResponse>({
    endpoint: `accounts-payable/suppliers/${supplierId}`,
    queryParameters: queryParams,
    options: {
      enabled: !!supplierId && enabled,
    },
  });
};

export default useSupplier;
